import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { openPdf } from "../../lib/openPdf";
import { toast } from "sonner";
import AdminNav from "../../components/AdminNav";
import StatusPill from "../../components/StatusPill";
import { CommercialChip } from "../../components/CommercialChip";
import { fmtDT, fmtINR, nextAction, isExceptionState } from "../../lib/constants";
import { ArrowLeft, Package as PackageIcon } from "@phosphor-icons/react";

export default function ClientShipments() {
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      api.get("/admin/clients"),
      api.get(`/admin/clients/${companyId}/shipments`),
    ])
      .then(([clientsRes, shipRes]) => {
        if (!active) return;
        const match = clientsRes.data.find((c) => c.id === companyId);
        setCompany(match || null);
        setShipments(shipRes.data);
      })
      .catch((e) => active && setError(e.displayMessage || "Failed to load client shipments"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [companyId]);

  return (
    <div className="max-w-[1600px] mx-auto p-6" data-testid="client-shipments-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <Link to="/admin/clients" className="overline inline-flex items-center gap-1 underline" data-testid="client-shipments-back">
            <ArrowLeft size={12} weight="bold" /> CLIENTS
          </Link>
          <h1 className="heading text-3xl" data-testid="client-shipments-company-name">
            {company ? company.legal_name : "Client"}
          </h1>
          {company && (
            <div className="mono text-xs text-[hsl(var(--muted))]" data-testid="client-shipments-company-id">
              {company.public_company_id} · {company.status}
            </div>
          )}
          <button className="btn-ghost text-xs mt-2" data-testid="client-agreement-download"
                  onClick={() => openPdf(`/admin/clients/${companyId}/agreement/document`)
                    .catch((e) => toast.error(e?.response?.status === 404 ? "No accepted Sender Agreement on record for this client." : "Could not open the document."))}>
            Sender Agreement (PDF)
          </button>
        </div>
        <AdminNav />
      </div>

      <div className="grid-card grid-card-strong">
        {loading ? (
          <div className="p-10 mono text-sm" data-testid="client-shipments-loading">Loading shipments…</div>
        ) : error ? (
          <div className="p-10 text-center text-sm text-[hsl(var(--danger))]" data-testid="client-shipments-error">{error}</div>
        ) : shipments.length === 0 ? (
          <div className="p-10 text-center" data-testid="client-shipments-empty">
            <PackageIcon size={32} weight="duotone" className="mx-auto mb-3 opacity-50" />
            <div className="overline mb-1">NO SHIPMENTS</div>
            <p className="text-sm text-[hsl(var(--muted))]">This client has no shipments yet.</p>
          </div>
        ) : (
          <table className="table-air12">
            <thead>
              <tr>
                <th>Shipment Number</th>
                <th>Route</th>
                <th>Status</th>
                <th>Commercial</th>
                <th>Next action</th>
                <th>Chargeable Wt</th>
                <th>Total Payable</th>
                <th>Created</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {shipments.map((s) => (
                <tr key={s.id} data-testid={`shipment-row-${s.public_shipment_number}`}>
                  <td>
                    <Link to={`/admin/shipments/${s.id}`} className="id-chip id-chip-strong">{s.public_shipment_number}</Link>
                    {isExceptionState(s.current_status) && (
                      <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[hsl(var(--error)/0.12)] text-[hsl(var(--error))]" data-testid={`exception-badge-${s.public_shipment_number}`}>
                        EXCEPTION
                      </span>
                    )}
                  </td>
                  <td><span className="id-chip">{s.route_code}</span></td>
                  <td><StatusPill status={s.current_status} /></td>
                  <td><CommercialChip shipment={s} role="SUPER_ADMIN" /></td>
                  <td className="text-xs">{nextAction(s.current_status)}</td>
                  <td className="mono">{s.total_chargeable_weight_kg} kg</td>
                  <td className="mono">{fmtINR(s.total_payable)}</td>
                  <td className="mono text-xs text-[hsl(var(--muted))]">{fmtDT(s.created_at)}</td>
                  <td>
                    <Link to={`/admin/shipments/${s.id}`} className="text-xs underline font-semibold" data-testid={`view-${s.public_shipment_number}`}>
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
